import { useEffect, useState } from "react";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../core/auth";
import { gql } from "../../../__generated__";
import type {
  GetStoreByAdminForMasterLoginQuery,
  GetStoreByAdminForMasterLoginQueryVariables,
  GetStoreListQuery,
} from "../../../__generated__/graphql";
import { GET_STORE_LIST } from "../store-signup-request/page";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

export const SELLER_LOGIN = gql(`
  mutation masterLogin($storeId: Int!) {
    masterLogin(storeId: $storeId) {
      token
      error
    }
  }
`);

const GET_STORE_BY_ADMIN_FOR_MASTER_LOGIN = gql(`
  query GetStoreByAdminForMasterLogin($storeId: Int!) {
    getStoreByAdmin(storeId: $storeId) {
      id
      title
    }
  }
`);

type StoreRow = NonNullable<
  GetStoreListQuery["getStoreList"]
>["result"][number];

type DeeplinkState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ok"; store: { id: number; title: string } }
  | { status: "notfound" };

const AdminMasterLogin = () => {
  const [searchParams] = useSearchParams();
  const storeId = searchParams.get("storeId");
  const navigate = useNavigate();
  const { loginAdmin } = useAuth();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [deeplink, setDeeplink] = useState<DeeplinkState>({ status: "idle" });
  const [getStoreByAdmin] = useLazyQuery<
    GetStoreByAdminForMasterLoginQuery,
    GetStoreByAdminForMasterLoginQueryVariables
  >(GET_STORE_BY_ADMIN_FOR_MASTER_LOGIN);

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data, loading, error } = useQuery<GetStoreListQuery>(GET_STORE_LIST);
  const [login, { loading: loggingIn }] = useMutation(SELLER_LOGIN);

  const fullList = data?.getStoreList?.ok
    ? (data.getStoreList.result ?? [])
    : [];
  const keyword = searchKeyword.trim().toLowerCase();
  const list = keyword
    ? fullList.filter(
        (store) =>
          store.title.toLowerCase().includes(keyword) ||
          (store.address?.toLowerCase().includes(keyword) ?? false),
      )
    : fullList;

  const handleLogin = async (store: StoreRow | { id: number }) => {
    try {
      const response = await login({
        variables: { storeId: store.id },
      });

      const { token, error: err } = response.data?.masterLogin ?? {};

      if (err) {
        alert(err);
      } else if (token) {
        localStorage.setItem("sellerToken", token);
        await loginAdmin();
        navigate("/boss/home");
      }
    } catch (err) {
      console.error("Error during login:", err);
      alert("로그인 중 오류가 발생했습니다.");
    }
  };

  useEffect(() => {
    if (!storeId) {
      setDeeplink({ status: "idle" });
      return;
    }
    setDeeplink({ status: "loading" });
    const id = parseInt(storeId, 10);
    if (Number.isNaN(id)) {
      setDeeplink({ status: "notfound" });
      return;
    }
    void getStoreByAdmin({
      variables: { storeId: id },
      onCompleted: (res) => {
        const s = res?.getStoreByAdmin;
        if (s) {
          setDeeplink({
            status: "ok",
            store: { id: s.id, title: s.title },
          });
        } else {
          setDeeplink({ status: "notfound" });
        }
      },
      onError: () => {
        setDeeplink({ status: "notfound" });
      },
    });
  }, [storeId, getStoreByAdmin]);

  if (storeId) {
    if (deeplink.status === "loading" || deeplink.status === "idle") {
      return (
        <div className="w-full p-4">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      );
    }
    if (deeplink.status === "notfound") {
      return (
        <div className="flex flex-col items-center justify-center h-screen gap-6 px-4">
          <p className="text-red-600 text-center">
            매장을 찾을 수 없습니다. 링크의 매장 ID를 확인하거나 목록에서 선택해 주세요.
          </p>
          <button
            type="button"
            className="bg-blue-600 text-white px-4 py-2 rounded-md"
            onClick={() => navigate("/admin/master-login")}
          >
            매장 목록으로
          </button>
        </div>
      );
    }
    if (deeplink.status === "ok") {
      const { store: storeFromUrl } = deeplink;
      return (
        <div className="flex flex-col items-center justify-center h-screen">
          <h1 className="text-2xl font-bold mb-8">
            <span className="text-blue-500">{storeFromUrl.title}</span> 매장에
            로그인할까요?
          </h1>
          <div className="flex gap-4">
            <button
              type="button"
              className="bg-gray-500 text-white p-2 rounded"
              onClick={() => navigate("/admin/master-login")}
            >
              아니오
            </button>
            <button
              type="button"
              className="bg-blue-500 text-white p-2 rounded"
              onClick={() => handleLogin({ id: storeFromUrl.id })}
            >
              로그인
            </button>
          </div>
        </div>
      );
    }
  }

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }
  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "목록을 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="매장 마스터 로그인"
      description="매장을 선택한 뒤 해당 매장 사장님 계정으로 로그인하세요."
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명·주소로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={list.map((s) => ({
              id: s.id,
              title: s.title,
              img: s.img,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) =>
              setSelectedStore(list.find((x) => x.id === store.id) ?? null)
            }
            emptyText={
              keyword
                ? "검색 결과가 없습니다."
                : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "로그인",
        emptyText: "매장을 선택하세요.",
        children: selectedStore ? (
          <div className="space-y-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">선택 매장:</span>{" "}
              {selectedStore.title} (ID: {selectedStore.id})
            </div>

            <div className="flex items-center gap-3">
              {selectedStore.img ? (
                <ResponsiveCDNImage
                  src={selectedStore.img}
                  alt={selectedStore.title}
                  className="w-16 h-16 rounded-md object-cover"
                  sizeRatio={0.16}
                />
              ) : (
                <div className="w-16 h-16 bg-gray-200 rounded-md" />
              )}
              <div>
                <div className="font-medium text-gray-800">
                  {selectedStore.title}
                </div>
                <div className="text-sm text-gray-600">
                  {selectedStore.address ?? "—"}
                </div>
              </div>
            </div>

            <button
              type="button"
              disabled={loggingIn}
              onClick={() => handleLogin(selectedStore)}
              className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loggingIn ? "로그인 중..." : "로그인"}
            </button>
          </div>
        ) : null,
      }}
    />
  );
};

export default AdminMasterLogin;
